import React, { useCallback, useEffect, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';

type AmountFieldProps = {
  /** 現在の値（数値文字列、空文字=未入力） */
  value: string;
  /** 値変更ハンドラ */
  onChange: (value: string) => void;
  /** ラベル */
  label?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 必須表示 */
  required?: boolean;
  /** 増減ステップ一覧（デフォルト: [1000, 10000, 100000]） */
  steps?: number[];
};

/**
 * 金額入力コンポーネント
 *
 * - 複数単位（1000/10000/100000）で増減するボタン付き
 * - 入力状態でない場合は3桁ごとのカンマを表示する
 * - 数値のみ入力可能
 */
const AmountField: React.FC<AmountFieldProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  steps = [1000, 10000, 100000],
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value);
    }
  }, [value, isFocused]);

  const formatWithComma = (val: string): string => {
    if (!val) return '';
    const num = Number(val);
    if (isNaN(num)) return val;
    return num.toLocaleString('ja-JP');
  };

  const formatStep = (step: number): string => {
    if (step >= 100000) return `${step / 10000}万`;
    if (step >= 10000) return `${step / 10000}万`;
    if (step >= 1000) return `${step / 1000}千`;
    return String(step);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const cleaned = localValue.replace(/[^0-9-]/g, '');
    if (cleaned === '' || cleaned === '-') {
      onChange('');
    } else {
      const num = parseInt(cleaned, 10);
      onChange(isNaN(num) ? '' : String(num));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-' || /^-?\d+$/.test(raw)) {
      setLocalValue(raw);
    }
  };

  const handleStep = useCallback(
    (step: number, direction: 'up' | 'down') => {
      const current = value ? Number(value) : 0;
      const next = direction === 'up' ? current + step : current - step;
      onChange(String(next < 0 ? 0 : next));
    },
    [value, onChange],
  );

  const displayValue = isFocused ? localValue : formatWithComma(value);

  return (
    <Stack spacing={0.5}>
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        label={label}
        required={required}
        error={error}
        helperText={helperText}
        disabled={disabled}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        slotProps={{
          input: {
            endAdornment:
              !isFocused && value ? <InputAdornment position="end">円</InputAdornment> : undefined,
          },
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
      {!disabled && (
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
          {steps.map((step, index) => (
            <React.Fragment key={step}>
              {index > 0 && (
                <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', flexShrink: 0 }} />
              )}
              <Stack direction="row" spacing={0.25} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleStep(step, 'down')}
                  disabled={disabled}
                  sx={{
                    minWidth: 28,
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 11,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                  }}
                >
                  <RemoveIcon sx={{ fontSize: 14 }} />
                </Button>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 0.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                    {formatStep(step)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleStep(step, 'up')}
                  disabled={disabled}
                  sx={{
                    minWidth: 28,
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 11,
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none' },
                  }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </Button>
              </Stack>
            </React.Fragment>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default AmountField;
